import { useRef, useEffect } from "react";
import { useToast } from "@chakra-ui/react";
import { useLocation } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";

import { PHONE_ERROR_MESSAGES } from "../utils";
import { usePhoneFormState } from "./usePhoneFormState";
import { usePersistStore } from "../../../../../store/persistStore";
import { useLoginFormStore } from "../../../../../store/loginFormStore";
import { EPhoneFormState } from "../../../../../store/slices/phoneFormSliceCreator";
import { isNewAuthorization, useAuthorizationStore } from "../../../../../store/authorizationStore";
import { NetworkError, otpRequestMutationFn } from "../../../../../api";
import { AUTH_MESSAGES, newAuthRequest, isIdentifier } from "../../../../../api/newAuthorization";

const toastId = "backend-error";

/**
 * Executes an OTP request mutation.
 * This method is responsible for handling the response of the OTP mutation request.
 * It updates the phone form state and shows appropriate error messages or navigation.
 */
export function useOTPMutationReaction() {
  const { pathname } = useLocation();
  const requestRef = useRef<{ controller: AbortController; epoch: number }>();

  useEffect(() => {
    return () => {
      requestRef.current?.controller.abort();
    };
  }, [pathname]);

  const toast = useToast();
  const phoneForm = usePhoneFormState();
  const resetTimer = usePersistStore(state => state.resetTimer);

  const sendOtp = useMutation({
    mutationKey: ["requestOTP"],
    retry: false,
    networkMode: isNewAuthorization() ? "always" : "online",
    mutationFn: async (params: { phone: string }) => {
      if (!isNewAuthorization()) {
        return otpRequestMutationFn(params);
      }

      const controller = new AbortController();
      const epoch = useAuthorizationStore.getState().epoch;
      requestRef.current = { controller, epoch };
      const data = await newAuthRequest("/otps", { phoneNumber: `+7${params.phone}` }, "POST", undefined, controller.signal);

      if (!isIdentifier(data.otpSessionUuid)) {
        throw new Error(AUTH_MESSAGES.phone);
      }

      if (!controller.signal.aborted && useAuthorizationStore.getState().epoch === epoch) {
        // Starting from phone resets the next steps; an SMS resend keeps the entered code.
        if (pathname === "/login") {
          const loginForm = useLoginFormStore.getState();
          loginForm.clearSMSForm();
          loginForm.clearForm();
        }
        useAuthorizationStore.setState({ otpSessionUuid: data.otpSessionUuid, authSessionId: undefined, flow: undefined, passportChecked: false });
      }

      return data;
    },
  });

  useEffect(() => {
    if (isNewAuthorization()) {
      const request = requestRef.current;
      if (!request || request.controller.signal.aborted || request.epoch !== useAuthorizationStore.getState().epoch) {
        return;
      }

      if (sendOtp.status === "success") {
        resetTimer();
        phoneForm.setFormState(EPhoneFormState.submitted);
      } else if (sendOtp.status === "error") {
        if (pathname === "/login/otp") {
          toast({ description: AUTH_MESSAGES.phone });
        } else {
          phoneForm.setErrorMessage(AUTH_MESSAGES.phone);
          phoneForm.setFormState(EPhoneFormState.initial);
        }
      }

      return;
    }
    switch (sendOtp.status) {
      case "success": {
        resetTimer();
        phoneForm.setFormState(EPhoneFormState.submitted);
        return;
      }
      case "error": {
        const error = sendOtp.error as NetworkError;
        // 2000 - validation
        // 7006 - already submitted
        if (error.status === 456) {
          switch (error.internalCode) {
            case 7006: {
              // Corner case. SMS already delivered to user and
              // API in cool down now, but user still trying to
              // submit same phone number
              phoneForm.setFormState(EPhoneFormState.submitted);
              // api doesn't return SMS cooldown time from the API
              // so, we're just resetting timer here. Because there is no other way
              // to get this time.
              resetTimer();
              return;
            }
            case 2000: {
              phoneForm.setFormState(EPhoneFormState.initial);
              phoneForm.setErrorMessage(PHONE_ERROR_MESSAGES.default);
              return;
            }
          }
        }
        // all other cases
        // Unexpected server error (.e.g 500) occurred
        if (!toast.isActive(toastId)) {
          toast({
            id: toastId,
            description: PHONE_ERROR_MESSAGES.unexpectedServerError,
          });
        }
        phoneForm.setFormState(EPhoneFormState.initial);
        return;
      }
      default:
        return;
    }
  }, [sendOtp.status]);

  return sendOtp;
}
